import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSupabaseClient } from "../lib/supabase";
import UnderReview from '../onboarding/UnderReview';
import ConsultationScheduled from '../onboarding/ConsultationScheduled';
import CompleteProfile from '../onboarding/CompleteProfile';
import BasicInfo from '../onboarding/BasicInfo';
import ExperienceForm from '../onboarding/ExperienceForm';
import ExperienceFormOld from '../onboarding/ExperienceForm.old';
import ClientCapacityCare from '../onboarding/ClientCapacityCare';
import PendingApproval from '../onboarding/PendingApproval';
import NotAFit from '../onboarding/NotAFit';
import FullApproval from '../onboarding/FullApproval';
import Chrome from '../onboarding/Chrome';
import WelcomeScreen from '../onboarding/WelcomeScreen';

export default function OnboardingRouter() {
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<'welcome' | 'basic' | 'client-capacity' | 'experience-old' | 'experience' | 'status-flow' | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [handledLocationKey, setHandledLocationKey] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const env = (window as any).__ENV__;
  const sb = getSupabaseClient();

  // Check if navigating back from experience form to old experience form
  const returnToOldExperience = (location.state as { returnToOldExperience?: boolean })?.returnToOldExperience;

  useEffect(() => {
    async function checkOnboardingCompletion() {
      // If navigating back from experience form (and this specific navigation hasn't been handled)
      if (returnToOldExperience && location.key !== handledLocationKey) {
        setCurrentStep('experience-old');
        setLoading(false);
        setHandledLocationKey(location.key);
        return;
      }

      // Skip database check if we already handled this back navigation
      if (returnToOldExperience && location.key === handledLocationKey) {
        return;
      }

      const {
        data: { user },
      } = await sb.auth.getUser();

      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check if user has accepted terms first
        const { data: profile } = await sb
          .from('profiles')
          .select('terms_accepted')
          .eq('id', user.id)
          .single();

        if (profile && !profile.terms_accepted) {
          navigate('/terms');
          setLoading(false);
          return;
        }

        // Check each onboarding section sequentially

        // 1. Check if user has started onboarding (show welcome screen if not)
        // First check DB for existing onboarding data — if any exists, they've started
        let hasOnboardingData = false;
        try {
          const { data: basicInfo_check } = await sb
            .from('basic_info')
            .select('user_id')
            .eq('user_id', user.id)
            .maybeSingle();
          if (basicInfo_check) hasOnboardingData = true;
        } catch {
          // Ignore — fall back to localStorage
        }

        const hasStartedOnboarding = hasOnboardingData || localStorage.getItem('onboarding_started');

        if (!hasStartedOnboarding) {
          setCurrentStep('welcome');
          setLoading(false);
          return;
        }

        // 2. Check if basic info is complete
        const { data: basicInfo } = await sb
          .from('basic_info')
          .select('*')
          .eq('user_id', user.id)
          .single();

        // If basic_info doesn't exist or is incomplete, show basic info form
        if (!basicInfo || !basicInfo.location || !basicInfo.bio) {
          setCurrentStep('basic');
          setLoading(false);
          return;
        }

        // 3. Check client capacity & care
        const { data: clientCapacity } = await sb
          .from('guide_client_capacity')
          .select('*')
          .eq('guide_id', user.id)
          .single();

        // If client capacity doesn't exist or is incomplete, show client capacity form
        if (!clientCapacity ||
            !clientCapacity.consultations_per_week ||
            !clientCapacity.new_clients_per_month ||
            !clientCapacity.platform_commitment ||
            !clientCapacity.service_fee_acceptance) {
          setCurrentStep('client-capacity');
          setLoading(false);
          return;
        }

        // 4. Check old experience form (guide_professional table only)
        const { data: professional } = await sb
          .from('guide_professional')
          .select('*')
          .eq('guide_id', user.id)
          .single();

        // Check if all required fields are completed for guide_professional
        // Note: is_main_income is not collected in the form, so we don't check for it
        if (!professional ||
            !professional.years_practicing ||
            !professional.employment_type ||
            !professional.ceremonies_count ||
            professional.has_medical_director === null ||
            professional.works_with_other_facilitators === null ||
            !professional.gender_id) {
          setCurrentStep('experience-old');
          setLoading(false);
          return;
        }

        // 5. Check new experience form (guide_professional_experience table)
        const { data: professionalExperience } = await sb
          .from('guide_professional_experience')
          .select('*')
          .eq('guide_id', user.id)
          .single();

        if (!professionalExperience) {
          setCurrentStep('experience');
          setLoading(false);
          return;
        }

        // 6-9. Enhanced profile, references, background check, and packages
        // will be checked in CompleteProfile component

        // All basic sections completed, check guide status
        const { data: guide, error } = await sb
          .from('guides')
          .select('status')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error loading status:', error);
        } else {
          setStatus(guide?.status);
          setCurrentStep('status-flow');
        }
      } catch (error) {
        console.error('Error checking onboarding completion:', error);
      } finally {
        setLoading(false);
      }
    }

    checkOnboardingCompletion();
  }, [refreshKey, returnToOldExperience, location.key, handledLocationKey]); // Dependencies for onboarding check

  // Function to refresh the onboarding status
  const refreshOnboarding = () => {
    // Clear any navigation state and refresh
    navigate('/onboarding', { replace: true, state: {} });
    setRefreshKey(prev => prev + 1);
  };

  if (loading) return <p>Loading...</p>;

  // Render welcome screen
  if (currentStep === 'welcome') {
    return <WelcomeScreen onGetStarted={refreshOnboarding} />;
  }

  // Render basic info step
  if (currentStep === 'basic') {
    return (
      <Chrome stepIndex={0}>
        <BasicInfo onComplete={refreshOnboarding} />
      </Chrome>
    );
  }

  // Render client capacity & care step
  if (currentStep === 'client-capacity') {
    return (
      <Chrome stepIndex={1}>
        <ClientCapacityCare onComplete={refreshOnboarding} />
      </Chrome>
    );
  }

  // Render old experience step (guide_professional)
  if (currentStep === 'experience-old') {
    return (
      <Chrome stepIndex={2}>
        <ExperienceFormOld onComplete={refreshOnboarding} />
      </Chrome>
    );
  }

  // Render new experience step (guide_professional_experience)
  if (currentStep === 'experience') {
    return (
      <Chrome stepIndex={2}>
        <ExperienceForm onComplete={refreshOnboarding} />
      </Chrome>
    );
  }

  // Render status-based flow
  if (currentStep === 'status-flow') {
    if (!status) return <p>Loading...</p>;

    if (status === 'pending') {
      return (
        <Chrome stepIndex={3}>
          <UnderReview />
        </Chrome>
      );
    }

    if (status === 'pending_activation') {
      return (
        <Chrome stepIndex={3}>
          <PendingApproval />
        </Chrome>
      );
    }

    if (status === 'consult_required') {
      return (
        <Chrome stepIndex={3}>
          <ConsultationScheduled />
        </Chrome>
      );
    }

    if (status === 'approved') {
      return (
        <Chrome stepIndex={4}>
          <CompleteProfile />
        </Chrome>
      );
    }

    if (status === 'active') {
      return <FullApproval />;
    }

    if (status === 'not_fit') {
      return (
        <Chrome stepIndex={4}>
          <NotAFit />
        </Chrome>
      );
    }

    return <p>Unknown status</p>;
  }

  return <p>Loading...</p>;
}
